import { cn } from '@workspace/ui/lib/utils'
import { Fragment, type ReactNode } from 'react'

export interface TableColumn<T> {
  header: string
  width?: string
  render: (item: T) => ReactNode
}

// --- Cell helper components for consistent table cell styling ---

/** Primary text (e.g. names, titles) */
export function CellPrimary({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn('font-medium', className)}>{children}</span>
}

/** Secondary/muted text — renders dash for nullish children */
export function CellText({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn('text-muted-foreground', className)}>{children ?? '-'}</span>
}

interface DataTableProps<T> {
  columns: TableColumn<T>[]
  data: T[]
  isLoading: boolean
  emptyMessage?: string
  keyExtractor: (item: T) => string
  /**
   * Width below which the table scrolls horizontally instead of compressing.
   *
   * The layout is `table-fixed`, so once the columns no longer fit, the ones
   * without an explicit width are squeezed until their content overlaps rather
   * than the table overflowing. A table with many columns needs to raise this
   * so it scrolls first.
   */
  minWidth?: string
  /**
   * Optional detail rendered in a full-width row directly beneath an item.
   *
   * Return null for rows with nothing to expand. Expansion state stays with the
   * caller — the table has no idea what "open" means, and a row that has to
   * fetch its own detail needs to own the request anyway.
   */
  renderExpanded?: (item: T) => ReactNode
}

export function DataTable<T>({
  columns,
  data,
  isLoading,
  emptyMessage = 'No results found.',
  keyExtractor,
  minWidth = '960px',
  renderExpanded,
}: DataTableProps<T>) {
  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <div className="overflow-x-auto">
        <table className="w-full table-fixed text-sm" style={{ minWidth }}>
          <thead>
            <tr className="border-b border-border bg-card text-left text-muted-foreground">
              {columns.map((col) => (
                <th
                  key={col.header}
                  className="px-4 py-3 font-medium"
                  style={col.width ? { width: col.width } : undefined}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-8 text-center text-muted-foreground"
                >
                  Loading...
                </td>
              </tr>
            )}
            {!isLoading && data.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-8 text-center text-muted-foreground"
                >
                  {emptyMessage}
                </td>
              </tr>
            )}
            {data.map((item) => {
              const expanded = renderExpanded?.(item)
              return (
                <Fragment key={keyExtractor(item)}>
                  <tr
                    className={cn(
                      'border-b border-border last:border-b-0',
                      // The detail row carries the separator when one is open,
                      // so the pair reads as a single block.
                      expanded && 'border-b-0'
                    )}
                  >
                    {columns.map((col) => (
                      <td key={col.header} className="px-4 py-3">
                        {col.render(item)}
                      </td>
                    ))}
                  </tr>
                  {expanded && (
                    <tr className="border-b border-border last:border-b-0">
                      <td colSpan={columns.length} className="px-4 pb-4">
                        {expanded}
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
